import { useTracking } from 'react-tracking';
import { push } from 'redux-first-router';

import { useMount } from '@simplywallst/ui-core';

import { ROUTE_PUBLIC_NARRATIVE_EDIT } from '@/constants/routes';
import { getPath } from '@/router/utils';

import { useSummaryV2 } from '@/hooks/useCompany';
import useUser from '@/hooks/useUser';

import {
  CreateNarrativeInput,
  useCreatePublicNarrative,
} from '../../hooks/useCreatePublicNarrative';
import {
  getCompanyFinancialData,
  getDefaultValuatorData,
} from '../../utils/calculation';
import { validateNarrativeForm } from '../../utils';

import type { NarrativeValuationCompany } from '../../hooks/useNarrativeValuationCompany';
import { hasErrors, useNarrativeForm } from '../../hooks/useNarrativeForm';
import { useNarrativeErrorToast } from '../../hooks/useNarrativeErrorToast';
import { NarrativeForm } from '../../utils/schema';
import { useNarrativeTracking } from '../../hooks/useNarrativeTracking';
import { NarrativeFormContent } from '../NarrativeFormContent';
import { transformFormValuesToValuation } from '../../utils/transformation';
import { useNarrativeBack } from '../../hooks/useNarrativeBack';
import { useMemo } from 'react';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

export type Props = {
  company: NarrativeValuationCompany;
  initialUserHoldsPosition: boolean;
};

export const PublicNarrativeCreate = ({
  company,
  initialUserHoldsPosition,
}: Props) => {
  const { data: user } = useUser();
  const summary = useSummaryV2(company.id);

  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'public-narrative',
    userId: user?.id,
    companyId: company.id,
  });
  const { referrer, goBack } = useNarrativeBack();

  const { mutate: createNarrative, status: createNarrativeStatus } =
    useCreatePublicNarrative({
      onSuccess: (narrativeId) => {
        const path = getPath({
          type: ROUTE_PUBLIC_NARRATIVE_EDIT,
          payload: { id: narrativeId },
          query: referrer
            ? {
                company: company.id,
                referrer,
              }
            : {},
        });
        trackEvent({
          action: 'save',
        });
        push(path);
        goBack();
      },
    });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  const defaultValuatorData = useMemo(() => {
    const companyFinancialData = getCompanyFinancialData(company);
    return getDefaultValuatorData(companyFinancialData);
  }, [company]);

  const initialNarrativeForm: NarrativeForm = {
    ...defaultValuatorData,
    userHoldsPosition: initialUserHoldsPosition,
    title: '',
    content: {
      text: '',
      html: '',
    },
  };

  const {
    values,
    setPartialValues,
    errors,
    setSubmitCount,
    submitCount,
    validateForm,
    dirty,
  } = useNarrativeForm({
    initialValues: initialNarrativeForm,
    validate: validateNarrativeForm,
  });
  useNarrativeErrorToast({ errors, submitCount });
  useNarrativeTracking({ trackEvent, dirty, errors });

  const userDisplayName = user.displayName || user.name;

  const handleSubmit = () => {
    setSubmitCount((c) => c + 1);
    if (hasErrors(validateForm())) return;

    if (typeof company !== 'undefined') {
      const { id, canonicalURL, analysisValue } = company;
      const { lastSharePrice } = analysisValue ?? {};
      const currencyIso = getTradingItemCurrencyISO(company?.data.currencyInfo);
      if (!currencyIso) throw new Error('Currency ISO is missing');
      const { content, title, userHoldsPosition } = values;

      const payload: CreateNarrativeInput = {
        // TODO can this be obtained based on companyId on BE?
        companyCanonicalUrl: canonicalURL,
        companyId: id,
        contentBlocks: content.html
          ? [
              {
                title: 'user_defined',
                contentHtml: content.html,
              },
            ]
          : [],
        // need to provide a fallback image in case coverImage is not available
        // TODO can this be obtained based on companyId on BE?
        coverImageUrl: summary.status === 'success' ? summary.coverImage : '',
        // TODO can this be obtained based on companyId on BE?
        currencyIso,
        // TODO to be removed - should be calculated on BE during publish
        publishDate: new Date().toISOString(),
        status: 'draft',
        title,
        userHoldsPosition,
        valuations: [
          transformFormValuesToValuation({
            values,
            lastSharePrice,
          }),
        ],
        // Unofficially deperecated in favour of status, schema needs to make it optional
        visibility: 'private',
      };

      createNarrative({ input: payload, setPrimary: true });
      setSubmitCount(0);
    }
  };

  const isLoading = createNarrativeStatus === 'pending';

  return (
    <Track>
      <NarrativeFormContent
        errors={errors}
        values={values}
        onChange={setPartialValues}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        submitLabel="Save"
        company={company}
        initialContentHtml={initialNarrativeForm.content.html}
        userDisplayName={userDisplayName}
        onReset={() => setPartialValues(initialNarrativeForm)}
      />
    </Track>
  );
};
